import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { api, type AppEvent } from "../api";
import { useAuth } from "../auth/AuthContext";
import {
  Column,
  isEnrichmentFailure,
  sortEventsNewestFirst,
  statusMeta,
} from "../components/AccountFeed";
import { EventDetailModal, summaryFromEvent } from "../components/EventDetailModal";
import { useToast } from "../components/Toast";
import { isElectronDesktop } from "../desktop";
import { useLiveEvents } from "../hooks/useLiveEvents";
import { useLocalEventsQuery } from "../hooks/localEventsStore";

function mergeById(base: AppEvent[], extra: AppEvent[]): AppEvent[] {
  const map = new Map<number, AppEvent>();
  for (const e of base) map.set(e.id, e);
  for (const e of extra) map.set(e.id, e);
  return [...map.values()];
}

const FAILED_OPTS = {
  status: "failed",
  limit: 2000,
  includeHidden: false,
} as const;

type FailTab = "new" | "processed";

/**
 * Enrichment failures (token present, GraphQL/enrich failed).
 * New → review; Mark processed → Processed tab.
 */
export function EnrichmentFailPage() {
  const { user, setAssignedKeys } = useAuth();
  const { push } = useToast();
  const [selected, setSelected] = useState<AppEvent | null>(null);
  const [filter, setFilter] = useState("");
  const [tab, setTab] = useState<FailTab>("new");
  const [httpSeed, setHttpSeed] = useState<AppEvent[]>([]);
  const [busyId, setBusyId] = useState<number | null>(null);
  const desktop = isElectronDesktop();
  const isAdmin = user?.role === "admin";

  const onAssignment = useCallback(
    (info: {
      resourceKey: string;
      message: string;
      assignedKeys: string[];
      events: AppEvent[];
    }) => {
      setAssignedKeys(info.assignedKeys);
      push(info.message, "ok");
    },
    [push, setAssignedKeys]
  );

  const onUnassignment = useCallback(
    (info: { resourceKey: string; message: string; assignedKeys: string[] }) => {
      setAssignedKeys(info.assignedKeys);
      push(info.message, "info");
    },
    [push, setAssignedKeys]
  );

  const { status, events: liveEvents, error } = useLiveEvents({
    enabled: Boolean(user) && isAdmin,
    onAssignment,
    onUnassignment,
  });

  const { events: localFailed } = useLocalEventsQuery(
    FAILED_OPTS,
    Boolean(desktop && isAdmin)
  );

  useEffect(() => {
    if (!isAdmin || desktop) return;
    let cancelled = false;
    void api<{ events: AppEvent[] }>("/api/data/events?afterId=0&limit=500")
      .then((data) => {
        if (!cancelled) {
          setHttpSeed((data.events || []).filter((e) => e.status === "failed"));
        }
      })
      .catch(() => {
        /* live merge still works */
      });
    return () => {
      cancelled = true;
    };
  }, [isAdmin, desktop]);

  const seed = desktop ? localFailed : httpSeed;
  const events = useMemo(() => mergeById(seed, liveEvents), [seed, liveEvents]);

  const failures = useMemo(
    () =>
      events
        .filter((e) => !e.hidden && isEnrichmentFailure(e))
        .sort(sortEventsNewestFirst),
    [events]
  );

  const newItems = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return failures
      .filter((e) => !e.enrichFailReviewed)
      .filter((e) => {
        if (!q) return true;
        const s = summaryFromEvent(e);
        const note = String(e.processNote || "").toLowerCase();
        return (
          s.email.toLowerCase().includes(q) ||
          s.id.toLowerCase().includes(q) ||
          note.includes(q)
        );
      });
  }, [failures, filter]);

  const processedItems = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return failures
      .filter((e) => Boolean(e.enrichFailReviewed))
      .filter((e) => {
        if (!q) return true;
        const s = summaryFromEvent(e);
        const note = String(e.processNote || "").toLowerCase();
        return (
          s.email.toLowerCase().includes(q) ||
          s.id.toLowerCase().includes(q) ||
          note.includes(q)
        );
      });
  }, [failures, filter]);

  const items = tab === "new" ? newItems : processedItems;

  async function setReviewed(ev: AppEvent, reviewed: boolean) {
    setBusyId(ev.id);
    try {
      const data = await api<{ event: AppEvent }>(
        `/api/admin/events/${ev.id}/enrich-fail-reviewed`,
        {
          method: "POST",
          body: JSON.stringify({ reviewed }),
        }
      );
      if (data.event && window.brexDesktop?.applyServerEvent) {
        void window.brexDesktop.applyServerEvent(data.event);
      }
      setSelected((cur) => (cur?.id === ev.id ? data.event : cur));
      push(reviewed ? "Moved to Processed" : "Moved back to New", "ok");
      if (reviewed && tab === "new") setSelected(null);
    } catch (err) {
      push(err instanceof Error ? err.message : "Update failed", "error");
    } finally {
      setBusyId(null);
    }
  }

  const meta = statusMeta(status);
  const selectedLive = selected
    ? events.find((e) => e.id === selected.id) || selected
    : null;

  if (user && user.role !== "admin") return <Navigate to="/" replace />;

  return (
    <div className="page page-wide page-fill">
      <header className="page-header">
        <div>
          <h1>Enrichment Fail</h1>
          <p className="muted page-blurb">
            Accounts where GraphQL enrichment failed. Review New, then mark Processed
            so fresh failures stay obvious.
          </p>
        </div>
        <div className="page-header-actions">
          <input
            className="search header-search"
            placeholder="Filter…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <span className={`status-chip ${meta.className}`}>
            <span className="status-dot" />
            {meta.label}
          </span>
          <span className="muted tiny">{items.length} items</span>
        </div>
      </header>

      {error && <div className="banner error">{error}</div>}

      <div className="stock-tabs" role="tablist" aria-label="Enrichment fail tabs">
        <button
          type="button"
          role="tab"
          aria-selected={tab === "new"}
          className={`stock-tab${tab === "new" ? " active" : ""}`}
          onClick={() => setTab("new")}
        >
          New
          <span className="muted tiny">{newItems.length}</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "processed"}
          className={`stock-tab${tab === "processed" ? " active" : ""}`}
          onClick={() => setTab("processed")}
        >
          Processed
          <span className="muted tiny">{processedItems.length}</span>
        </button>
      </div>

      <div className="feed-board feed-board-single">
        <Column
          title={tab === "new" ? "New failures" : "Processed failures"}
          items={items}
          empty={
            tab === "new"
              ? "No new enrichment failures."
              : "No processed enrichment failures yet."
          }
          isAdmin
          onOpen={setSelected}
          pageSize={25}
          resetKey={`${tab}|${filter}`}
        />
      </div>

      {selectedLive && (
        <EventDetailModal
          event={selectedLive}
          onClose={() => setSelected(null)}
          enrichFailMode
          enrichFailReviewed={Boolean(selectedLive.enrichFailReviewed)}
          enrichFailBusy={busyId === selectedLive.id}
          onMarkEnrichFailReviewed={(reviewed) =>
            void setReviewed(selectedLive, reviewed)
          }
        />
      )}
    </div>
  );
}
